import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import {
  OperationLog,
  OperationLogSource,
  OperationLogStatus,
} from '@/client/operation-log';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { ADRestorationsConfirmDialog } from '@/components/accounts/ADRestorationsConfirmDialog';
import { useAzureADHandler } from '@/hooks/integrations/useAzureAD.hook';
import { useOperationLogs } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useUsersImportFlow } from '@/hooks/useUsersImportFlow';
import { AdSyncErrorsSection } from '@/pages/accounts/account-errors/AdSyncErrorsSection';
import { CsvErrorsSection } from '@/pages/accounts/account-errors/CsvErrorsSection';
import {
  EmptyStateCard,
  EmptyStateContent,
  PageContainer,
} from '@/pages/accounts/account-errors/error-log.styled';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const pathItems = (
  accountName: string | undefined,
  accountId: string | undefined,
  t: (key: string) => string,
): MenuItem[] => {
  const items: MenuItem[] = [];

  if (accountName && accountId) {
    items.push({
      label: accountName,
      url: RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](accountId),
      template: AppBreadCrumbTemplate,
    });
  }

  items.push({
    label: t('errorLog.title'),
    url: RedirectPaths[RedirectPathsEnum.ACCOUNT_ERRORS](),
    template: AppBreadCrumbTemplate,
  });

  return items;
};

export const AccountErrorsPage = () => {
  const { t } = useTranslation();
  const currentAccount = useAppSelector(selectCurrentAccount);
  const navigate = useNavigate();
  const { renderFileUpload, importModal } = useUsersImportFlow({});
  const { operationLogs, isLoading } = useOperationLogs(currentAccount?.id);
  const sourcesWithErrorsOnMount = useRef<Set<OperationLogSource> | null>(null);
  const {
    handleForceADSync,
    syncState,
    restorations,
    showRestorationsDialog,
    handleRestorationsConfirm,
    handleOverwriteConfirm,
    handleRestorationsClose,
  } = useAzureADHandler(currentAccount?.id as string);

  const csvLog = operationLogs?.result?.find(
    (log) => log.source === OperationLogSource.MANUAL_USER_IMPORT,
  );
  const adLog = operationLogs?.result?.find(
    (log) => log.source === OperationLogSource.AD_SYNC,
  );

  if (
    !isLoading &&
    operationLogs &&
    sourcesWithErrorsOnMount.current === null
  ) {
    sourcesWithErrorsOnMount.current = new Set(
      operationLogs.result
        ?.filter(
          (log) =>
            log.status !== OperationLogStatus.SUCCESS && log.errorCount > 0,
        )
        .map((log) => log.source) ?? [],
    );
  }

  if (isLoading) {
    return (
      <FlexContainer direction="column" className="mt-5">
        <ProgressSpinner />
      </FlexContainer>
    );
  }

  const hasCurrentErrors = (log: OperationLog | undefined) =>
    !!log && log.status !== OperationLogStatus.SUCCESS && log.errorCount > 0;

  const showCsvSection =
    hasCurrentErrors(csvLog) ||
    !!sourcesWithErrorsOnMount.current?.has(
      OperationLogSource.MANUAL_USER_IMPORT,
    );

  const showAdSection =
    hasCurrentErrors(adLog) ||
    !!sourcesWithErrorsOnMount.current?.has(OperationLogSource.AD_SYNC);

  return (
    <PageContainer direction="column" align="flex-start">
      <AppBreadCrumb
        model={pathItems(currentAccount?.name, currentAccount?.id, t)}
      />

      <FlexContainer justify="space-between">
        <h1 className="mt-2 mb-2">{t('errorLog.title')}</h1>
      </FlexContainer>

      {showCsvSection && (
        <CsvErrorsSection
          log={csvLog}
          renderFileUpload={renderFileUpload}
          t={t}
        />
      )}

      {showAdSection && (
        <AdSyncErrorsSection
          log={adLog}
          t={t}
          onSync={() => handleForceADSync(currentAccount?.id as string)}
          syncState={syncState}
          onGoToSettings={() =>
            navigate(
              RedirectPaths[RedirectPathsEnum.ACCOUNT_INTEGRATIONS_MICROSOFT](),
            )
          }
        />
      )}

      {!showCsvSection && !showAdSection && (
        <EmptyStateCard>
          <EmptyStateContent>
            <i className="pi pi-check-circle" />
            <span>{t('errorLog.noCurrentErrors')}</span>
          </EmptyStateContent>
        </EmptyStateCard>
      )}

      <ADRestorationsConfirmDialog
        restorations={restorations}
        visible={showRestorationsDialog}
        onConfirm={handleRestorationsConfirm}
        onOverwrite={handleOverwriteConfirm}
        onClose={handleRestorationsClose}
      />

      {importModal}
    </PageContainer>
  );
};
